/**
 * Deal Flow API client — reads persisted opportunities only.
 * No provider calls happen on Deal Flow load; this wraps the existing
 * /product-sourcing/opportunities endpoint (Mongo-backed).

 * Bookmarking REUSES the existing workspace-scoped Product Save persistence
 * (sfh_saved_products) — the canonical Ezisell "save a Walmart product" action.
 * It never mutates the opportunity document's `updated_at`, so saving cannot
 * affect Deal Flow freshness / "recently updated" ordering / Live Feed.
 */
import { api } from "@/lib/api";
import { saveStorefrontProduct, unsaveStorefrontProduct } from "@/lib/storefrontHunter/api";
import { mapDealFlowLead } from "./adapter";

const LIMIT = 24;

/**
 * @param {object} params query params for the opportunities endpoint
 * @returns {Promise<{ leads: object[], total: number, lastUpdatedAt: string|null, page: number, hasMore: boolean }>}
 */
export async function fetchDealFlowLeads(params = {}) {
  const res = await api.productSourcingOpportunities({
    limit: params.limit ?? LIMIT,
    page: params.page ?? 1,
    sort: params.sort ?? "newest",
    ...params,
  });
  const data = res?.data ?? res ?? {};
  const items = Array.isArray(data.items) ? data.items : [];
  const total = Number(data.total ?? items.length);
  const leads = items.map(mapDealFlowLead);
  const page = Number(data.page ?? 1);
  const limit = Number(data.limit ?? LIMIT);
  return {
    leads,
    total,
    lastUpdatedAt: data.summary?.last_updated_at ?? null,
    page,
    hasMore: page * limit < total,
  };
}

/**
 * Persist the bookmark via the existing workspace-scoped Product Save endpoint.
 * `saved` (true=save, false=unsave) is the user's requested state. The list
 * response already carries the canonical `saved` flag (one batch join), so the
 * UI performs an idempotent optimistic update + rollback and does NOT issue an
 * extra per-save status fetch.
 *
 * Field semantics matter: sfh_saved_products is Storefront-Hunter-oriented.
 *   seller_name      = the Walmart storefront seller (NOT the external retailer)
 *   storefront_price = that Walmart seller's own offer price (NOT source price)
 *   buy_box_price    = the Walmart Buy Box price (only when guaranteed)
 *
 * Deal Flow's sourceMerchant/sourcePrice are EXTERNAL acquisition data (Target,
 * eBay, …) and are deliberately NOT written into those Walmart-semantic fields.
 * Only fields whose semantics are definitely compatible are persisted; the
 * product_sourcing `walmart.price` is a generic listing price, not a guaranteed
 * Buy Box price, so `buy_box_price` stays null.
 */
export async function saveOpportunity(lead, saved) {
  if (!lead?.walmartItemId) return null;
  if (saved) {
    return saveStorefrontProduct({
      walmart_item_id: lead.walmartItemId,
      title: lead.walmartTitle || lead.sourceTitle || null,
      image_url: lead.walmartImage || lead.sourceImage || null,
      brand: lead.walmartBrand || lead.sourceBrand || null,
      category: lead.category || null,
      // Never map external retailer/price into Walmart seller/storefront fields.
    });
  }
  return unsaveStorefrontProduct(lead.walmartItemId);
}
